"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import {
  type SlotFormValues,
  buildInitialSlotValues,
  buildSlotFields,
  buildSlotFormSchema,
  buildSlotGroups,
  buildSlotPayload,
} from '../../../lib/slotForm'
import { loadSlotLocationFieldOptions } from '../../../lib/locationSearch'
import { loadSlotCargoFieldOptions } from '../../../lib/cargoSearch'

export default function BookSlotPage() {
  const t = useT()
  const router = useRouter()

  const loadLocationOptions = React.useCallback(
    async (query?: string) =>
      loadSlotLocationFieldOptions(query).catch(() => {
        throw new Error(t('slots.pages.book.loadError', 'Failed to load booking options.'))
      }),
    [t],
  )

  const loadCargoOptions = React.useCallback(
    async (query?: string) =>
      loadSlotCargoFieldOptions(query).catch(() => {
        throw new Error(t('slots.pages.book.loadError', 'Failed to load booking options.'))
      }),
    [t],
  )

  const fields = React.useMemo(
    () => buildSlotFields(t, { loadLocations: loadLocationOptions, loadCargo: loadCargoOptions }),
    [loadCargoOptions, loadLocationOptions, t],
  )
  const groups = React.useMemo(() => buildSlotGroups(t), [t])
  const schema = React.useMemo(() => buildSlotFormSchema(t), [t])
  const initialValues = React.useMemo(() => buildInitialSlotValues(), [])

  return (
    <Page>
      <PageBody>
        <CrudForm<SlotFormValues>
          title={t('slots.pages.book.title', 'Book slot')}
          backHref="/backend/slots"
          cancelHref="/backend/slots"
          submitLabel={t('slots.pages.book.submit', 'Book slot')}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          schema={schema}
          onSubmit={async (values) => {
            const payload = buildSlotPayload(values)
            const created = await createCrud<{ id?: string }>('freight/slots/book', payload, {
              errorMessage: t('slots.pages.book.saveError', 'Failed to book slot.'),
            })
            flash(t('slots.pages.book.success', 'Slot booked successfully.'), 'success')
            const id = created.result?.id
            router.push(id ? `/backend/slots/${id}` : '/backend/slots')
          }}
        />
      </PageBody>
    </Page>
  )
}
